import type { CoverSheetConfigCard, CoverSheetOverrides } from '../../types';
import { applyOverrides } from '../utils/applyOverrides';

const buildBaseCardConfig = (contactType: string, contactLabel: string) => {
  return {
    id: `additionalContacts.${contactType}`,
    innerTitle: contactLabel,
    outerTitle: `${contactLabel} Contacts`,
    isAddable: true,
    addByDefault: false,
    hideByDefault: true,
    noEntryText: 'Add information',
    addText: 'Add',
    maxAddable: 2,
    entity: 'contacts',
    entityType: contactType,
    isCollapsible: true,
    syncsWithGoogleContacts: true,
    displayFields: [
      {
        name: `contacts.${contactType}.x.firstName`,
        type: 'name',
      },
      {
        name: `contacts.${contactType}.x.lastName`,
        type: 'name',
      },
      {
        name: `contacts.${contactType}.x.role`,
        type: 'default',
      },
      {
        name: `contacts.${contactType}.x.email`,
        type: 'email',
      },
      {
        name: `contacts.${contactType}.x.cellPhone`,
        type: 'phone',
      },
      {
        name: `contacts.${contactType}.x.companyName`,
        type: 'default',
      },
    ],
    collapsedName: [
      `contacts.${contactType}.x.firstName`,
      `contacts.${contactType}.x.lastName`,
    ],
    layout: [
      [
        {
          name: `contacts.${contactType}.x.role`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${contactType}.x.firstName`,
          size: 50,
        },
        {
          name: `contacts.${contactType}.x.lastName`,
          size: 50,
        },
      ],
      [
        {
          name: `contacts.${contactType}.x.email`,
          size: 50,
        },
        {
          name: `contacts.${contactType}.x.cellPhone`,
          size: 50,
        },
      ],
      [
        {
          name: `contacts.${contactType}.x.companyName`,
          size: 100,
        },
      ],
      [
        {
          name: `contacts.${contactType}.x.street1`,
          size: 75,
        },
        {
          name: `contacts.${contactType}.x.street2`,
          size: 25,
        },
      ],
      [
        {
          name: `contacts.${contactType}.x.city`,
          size: 50,
        },
        {
          name: `contacts.${contactType}.x.state`,
          size: 25,
          mobileSize: 50,
        },
        {
          name: `contacts.${contactType}.x.zip`,
          size: 25,
          mobileSize: 50,
        },
      ],
    ],
    fields: {
      role: {
        name: `contacts.${contactType}.x.role`,
        entity: 'contacts',
        entityType: contactType,
        type: 'text',
        label: 'Contact Type',
        placeholderText: 'Inspector, Assistant, etc',
      },
      firstName: {
        name: `contacts.${contactType}.x.firstName`,
        entity: 'contacts',
        entityType: contactType,
        type: 'autofill',
        label: 'First Name',
        placeholderText: '',
        action: {
          name: 'autofill',
          provider: 'googleContacts',
          label: {
            primary: 'fullName',
            secondary: 'email',
          },
          props: [
            {
              from: 'googleContactId',
              to: `contacts.${contactType}.x.googleContactId`,
            },
            {
              from: 'contactSource',
              to: `contacts.${contactType}.x.contactSource`,
            },
            {
              from: 'firstName',
              to: `contacts.${contactType}.x.firstName`,
            },
            {
              from: 'lastName',
              to: `contacts.${contactType}.x.lastName`,
            },
            {
              from: 'email',
              to: `contacts.${contactType}.x.email`,
            },
            {
              from: 'cellPhone',
              to: `contacts.${contactType}.x.cellPhone`,
            },
            {
              from: 'companyName',
              to: `contacts.${contactType}.x.companyName`,
            },
            {
              from: 'street1',
              to: `contacts.${contactType}.x.street1`,
            },
            {
              from: 'street2',
              to: `contacts.${contactType}.x.street2`,
            },
            {
              from: 'city',
              to: `contacts.${contactType}.x.city`,
            },
            {
              from: 'state',
              to: `contacts.${contactType}.x.state`,
            },
            {
              from: 'zip',
              to: `contacts.${contactType}.x.zip`,
            },
          ],
        },
      },
      lastName: {
        name: `contacts.${contactType}.x.lastName`,
        entity: 'contacts',
        entityType: contactType,
        type: 'text',
        label: 'Last Name',
        placeholderText: '',
      },
      email: {
        name: `contacts.${contactType}.x.email`,
        entity: 'contacts',
        entityType: contactType,
        type: 'text',
        label: 'Email',
        placeholderText: '',
        validation: [
          {
            type: 'email',
            message: 'Invalid Email',
          },
        ],
      },
      cellPhone: {
        name: `contacts.${contactType}.x.cellPhone`,
        entity: 'contacts',
        entityType: contactType,
        type: 'phoneWithExt',
        label: 'Phone',
        placeholderText: '',
        validation: [
          {
            type: 'phone',
            message: 'Invalid phone number',
          },
        ],
      },
      companyName: {
        name: `contacts.${contactType}.x.companyName`,
        entity: 'contacts',
        entityType: contactType,
        type: 'text',
        label: 'Company Name',
        placeholderText: '',
      },
      street1: {
        name: `contacts.${contactType}.x.street1`,
        entity: 'contacts',
        entityType: contactType,
        type: 'autofill',
        label: 'Street',
        placeholderText: '',
        action: {
          name: 'autofill',
          provider: 'googlePlacesSearch',
          label: {
            primary: 'label.primary',
            secondary: 'label.secondary',
          },
          props: [
            {
              from: 'street1',
              to: `contacts.${contactType}.x.street1`,
            },
            {
              from: 'street2',
              to: `contacts.${contactType}.x.street2`,
            },
            {
              from: 'city',
              to: `contacts.${contactType}.x.city`,
            },
            {
              from: 'state',
              to: `contacts.${contactType}.x.state`,
            },
            {
              from: 'county',
              to: `contacts.${contactType}.x.county`,
            },
            {
              from: 'zip',
              to: `contacts.${contactType}.x.zip`,
            },
          ],
        },
      },
      street2: {
        name: `contacts.${contactType}.x.street2`,
        entity: 'contacts',
        entityType: contactType,
        type: 'text',
        label: 'Apt/Unit/Suite Number',
        placeholderText: '',
      },
      city: {
        name: `contacts.${contactType}.x.city`,
        entity: 'contacts',
        entityType: contactType,
        type: 'text',
        label: 'City',
        placeholderText: '',
      },
      state: {
        name: `contacts.${contactType}.x.state`,
        entity: 'contacts',
        entityType: contactType,
        type: 'state',
        label: 'State',
        placeholderText: '',
      },
      zip: {
        name: `contacts.${contactType}.x.zip`,
        entity: 'contacts',
        entityType: contactType,
        type: 'zip',
        label: 'Zip Code',
        placeholderText: '',
      },
    },
  };
};

type BaseCardConfig = ReturnType<typeof buildBaseCardConfig>;

// Allow overriding known properties in BaseCardConfig plus any other properties in CoverSheetConfigCard
export type ContactCardOverrides = CoverSheetOverrides<BaseCardConfig> &
  Omit<CoverSheetConfigCard, keyof BaseCardConfig>;

/**
 * Builds an additional contact card for a given contact entity type.
 * @param options Options
 * @param options.contactType Contact entity type
 * @param options.contactLabel Contact label
 * @param options.overrides Overrides to apply to base card config
 * @returns Coversheet card config for additional contact
 */
export function buildAdditionalContactCard<
  T extends CoverSheetOverrides<BaseCardConfig>,
>({
  contactType = 'other',
  contactLabel = 'Other',
  overrides,
}: {
  contactType: string;
  contactLabel: string;
  overrides?: T;
}) {
  const baseConfig = buildBaseCardConfig(contactType, contactLabel);
  return applyOverrides(baseConfig, overrides);
}
